import { useCallback, useEffect } from "react";

// Loose type so the factory works with any Zustand store hook without
// importing Zustand's internal UseBoundStore / StoreApi types here.
type AnyStoreHook = (selector: (state: any) => any) => any;

/**
 * Low-level primitive: subscribes to the `storage` event and calls
 * `onStorageChange` whenever the given key is updated in another tab.
 * `onStorageChange` receives the raw new value string (or `null` if cleared).
 *
 * Wrap your callback in `useCallback` to keep the effect stable.
 *
 * @example
 * const handleChange = useCallback((newValue: string | null) => {
 *   if (!newValue) { clearData(); return; }
 *   const parsed = JSON.parse(newValue);
 *   doSomethingWith(parsed);
 * }, [clearData]);
 *
 * useStorageSync(STORAGE_KEY, handleChange);
 */
export function useStorageSync(storageKey: string, onStorageChange: (newValue: string | null) => void) {
	useEffect(() => {
		const handleStorageChange = (event: StorageEvent) => {
			if (event.key === storageKey) {
				onStorageChange(event.newValue);
			}
		};
		window.addEventListener("storage", handleStorageChange);
		return () => window.removeEventListener("storage", handleStorageChange);
	}, [storageKey, onStorageChange]);
}

/**
 * Factory that creates a store-specific `useSyncTabsEffect` hook for stores
 * that follow the standard `persist` + `setFilters/filters` pattern.
 *
 * Internally uses `useStorageSync`. For stores with custom sync logic
 * (e.g. non-filter state, null-handling, field exclusions), use
 * `useStorageSync` directly instead.
 *
 * @param storageKey  The `name` passed to `persist()` (i.e. the localStorage key).
 * @param useStore    The store's Zustand hook. State must expose `setFilters`.
 *
 * @example
 * export const useSyncTabsEffect = createSyncTabsHook("guide-filters", useGuideFiltersStore);
 *
 * // In a component:
 * useSyncTabsEffect();
 */
export function createSyncTabsHook<TFilters>(storageKey: string, useStore: AnyStoreHook): () => void {
	return function useSyncTabsEffect() {
		const setFilters = useStore(state => state.setFilters as (f: Partial<TFilters>) => void);

		const handleChange = useCallback(
			(newValue: string | null) => {
				if (!newValue) return;
				try {
					const newData = JSON.parse(newValue) as {
						state?: { filters?: Partial<TFilters> };
					};
					if (newData?.state?.filters) {
						setFilters(newData.state.filters);
					}
				} catch (error) {
					console.error("Error synchronizing tabs:", error);
				}
			},
			[setFilters]
		);

		useStorageSync(storageKey, handleChange);
	};
}
